import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { DateRangePicker, defaultStartDate, defaultEndDate, useIsMobile, } from '@cmd/ui-components';
import { complexStoreSet, fetchGet, formatDateYYYYMMDDDash, storeSet, } from '@cmd/ui-utilities';
import { SelectInput } from '@interstate/components/SelectInput';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getLogiToken } from '../../logi/logiEmbedManager';
import { updateDashboardsIdReceived } from '../../state/dashboardIdState';
import { organizationSelector } from '../../state/orgIdState';
import { USER_HAS_NO_DASHBOARDS } from '../../utils/constants';
import './LandingPageHeader.scss';
const startDateConfig = {
    defaultValue: formatDateYYYYMMDDDash(defaultStartDate),
};
const endDateConfig = {
    defaultValue: formatDateYYYYMMDDDash(defaultEndDate),
};
const dateRangeConfig = {
    startDate: startDateConfig,
    endDate: endDateConfig,
};
const { reducer: dateRangeReducer, selector: dateRangeSelector, updateAction: updateDateRange, urlUpdater: dateRangeUrlUpdater, } = complexStoreSet('dateRange', dateRangeConfig);
const DASHBOARDS_ENDPOINT = '/api/logi/dashboards';
const { reducer: dashboardTimeFieldsReducer, selector: dashboardTimeFieldsSelector, updateAction: updateDashboardTimeFields, } = storeSet('dashboardTimeFields', {});
const { reducer: selectedDashboardReducer, selector: selectedDashboardSelector, updateAction: updateSelectedDashboard, urlUpdater: selectedDashboardUrlUpdater, } = storeSet('selectedDashboard', '');
const LandingPageHeader = () => {
    const intl = useIntl();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const dateRange = useSelector(dateRangeSelector);
    const selectedDashboard = useSelector(selectedDashboardSelector);
    const orgId = useSelector(organizationSelector);
    const [dashboardOptions, setDashboardOptions] = useState([]);
    useEffect(() => {
        // 1. Get accessible dashboards for user
        // 2. Show deep linked dashboard if it exists and is accessible
        // 3. otherwise default to first dashboard in list
        // 4. If not dashboards are accessible, or something fails, show graceful error message in place of dashboard
        getLogiToken(orgId).then((token) => fetchGet(DASHBOARDS_ENDPOINT, '', undefined, {
            headers: { Authorization: `Bearer ${token.access_token}` },
        })).then((response) => {
            const returnedDashboards = response.data.content.map((d) => ({ value: d.id, label: d.name }));
            const selectedDashboardExists = returnedDashboards.some((d) => d.value === selectedDashboard);
            if (!selectedDashboardExists && returnedDashboards.length > 0) {
                dispatch(updateSelectedDashboard(returnedDashboards[0].value));
            }
            else if (returnedDashboards.length === 0) {
                // No dashboards are accessible, we need to notify the user with a graceful error message.
                dispatch(updateSelectedDashboard(USER_HAS_NO_DASHBOARDS));
            }
            setDashboardOptions(returnedDashboards);
            const timeFieldsMap = {};
            response.data.content.forEach((d) => {
                timeFieldsMap[d.id] = [...new Set(d.unifiedBarCfgs.map((cfg) => cfg.timeControlCfg.timeField))];
            });
            dispatch(updateDashboardTimeFields(timeFieldsMap));
            dispatch(updateDashboardsIdReceived(true));
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch dashboards:', error);
            // Set error state by stopping loading and showing "no dashboards" message
            dispatch(updateSelectedDashboard(USER_HAS_NO_DASHBOARDS));
        });
    }, []);
    return (_jsxs("section", { id: "landing-page-header", children: [_jsx("h1", { children: intl.formatMessage({
                    id: 'LANDING_PAGE_TITLE',
                    defaultMessage: 'Executive Summary',
                }) }), _jsxs("div", { className: "header-controls", children: [_jsx("div", { className: "dashboard-picker", children: _jsx(SelectInput, { name: "dashboard-picker", placeholder: selectedDashboard === USER_HAS_NO_DASHBOARDS
                                ? 'No Dashboards Available'
                                : 'Dashboard Selector', options: dashboardOptions, onChange: (event) => {
                                dispatch(updateSelectedDashboard(event.target.value));
                            }, displayDeselectOption: false, size: isMobile ? 'small' : 'medium', value: dashboardOptions.find((o) => selectedDashboard === o.value)?.value }) }), _jsx("div", { className: "date-range-picker", children: _jsx(DateRangePicker, { dateRange: dateRange, onChangeDateRange: (d) => dispatch(updateDateRange(d)), size: isMobile ? 'small' : 'medium' }) })] })] }));
};
export { dashboardTimeFieldsReducer, dashboardTimeFieldsSelector, dateRangeReducer, dateRangeSelector, dateRangeUrlUpdater, selectedDashboardReducer, selectedDashboardSelector, selectedDashboardUrlUpdater, };
export default LandingPageHeader;
